import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { numberLocale } from '@/lib/language';
import { useEnforcement } from '@/hooks/useEnforcement';
import { planTierLabel } from '@/lib/plans';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight } from 'lucide-react';

export const UsageWidget: React.FC = () => {
  const { t, i18n } = useTranslation('enforcement');
  const {
    count, limit, usagePct, phase, plan, totalProducts, isLoading,
    billedOwnerEmail, isMemberOfForeignMc, planKnown,
  } = useEnforcement();

  // Cold load with no cached snapshot: show a skeleton instead of the permissive
  // free/0/200 defaults, which would flash the wrong plan before the real one
  // arrives. With the localStorage seed (lib/planCache.ts) this only happens on a
  // user's very first load.
  if (isLoading) {
    return (
      <div className="px-4 py-3 space-y-2" data-testid="usage-widget-skeleton">
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-2.5 w-full" />
        <Skeleton className="h-2 w-24" />
      </div>
    );
  }

  const colorClass = phase === 'RED'
    ? 'text-destructive'
    : phase === 'YELLOW'
    ? 'text-warning'
    : 'text-success-ink';

  const progressColor = phase === 'RED'
    ? '[&>div]:bg-destructive'
    : phase === 'YELLOW'
    ? '[&>div]:bg-warning'
    : '[&>div]:bg-success';

  // Band-Tiers wuerden kapitalisiert "Cap1000" heissen — der Label kommt aus
  // PLANS_BY_TIER (Baender lokalisiert als "1.000 Produkte", Legacy-Eigennamen).
  const planLabel = planTierLabel(plan, i18n?.language, t);

  // MC-Mitgliedschaft Stufe 1 (Task 12, Fix round 1): vor Migration B
  // verweigert `check_can_optimize` eine fremde `p_user_id` und liefert eine
  // leere Menge — `count`/`limit`/`plan` sind dann reine Vorgabewerte
  // (0/200/free aus useEnforcement's fail-open), keine echten Werte des
  // Eigentuemers. Ein Mitglied saehe sonst "Kontingent von owner@…" direkt
  // neben "Tarif: Free" und "0 / 200" — ein Label mit erfundenen Zahlen,
  // praesentiert als das Kontingent des Eigentuemers. Solange `planKnown`
  // false ist, zeigen wir das Label OHNE jede Zahl (kein Plan, kein Count,
  // keine Fortschrittsleiste); nach Migration B (`planKnown` true) rendert
  // der normale Zweig mit den echten Zahlen.
  const showQuotaOfLine = isMemberOfForeignMc && !!billedOwnerEmail;
  const quotaNumbersUnknown = showQuotaOfLine && !planKnown;

  return (
    <div className="px-4 py-3 space-y-2">
      {/* MC-Mitgliedschaft Stufe 1 (Task 12): ist der Nutzer nur Mitglied der
          aktiven Merchant Center, gehoert das darunter gezeigte Kontingent
          dem Eigentuemer, nicht ihm selbst — das muss ueber der Zahl stehen,
          sonst liest sich "120 / 2.000" als das eigene Kontingent. */}
      {showQuotaOfLine && (
        <p
          className="text-[10px] text-muted-foreground"
          data-testid="usage-widget-quota-of"
        >
          {t('quotaOf', { email: billedOwnerEmail })}
        </p>
      )}
      {quotaNumbersUnknown ? (
        <p
          className="text-[10px] text-muted-foreground"
          data-testid="usage-widget-quota-pending"
        >
          {t('quotaPending')}
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Link to="/pricing" className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
              {t('usage.planLabel', { plan: planLabel })}
            </Link>
            <span className={`text-xs font-semibold ${colorClass}`}>
              {count.toLocaleString(numberLocale(i18n.language))} / {limit.toLocaleString(numberLocale(i18n.language))}
            </span>
          </div>
          <Progress value={Math.min(usagePct, 100)} className={`h-2.5 ${progressColor}`} />
          <p className="text-[10px] text-muted-foreground">
            {phase === 'RED'
              ? t('usage.limitReached')
              : t('usage.percentUsed', { percent: Math.round(usagePct) })}
          </p>
          {totalProducts > limit && (
            <p className="text-[10px] text-warning-ink">
              {t('usage.productsInFeeds', { count: totalProducts, formatted: totalProducts.toLocaleString(numberLocale(i18n.language)) })}
            </p>
          )}
        </>
      )}
      <Link
        to="/pricing"
        className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
      >
        {t('usage.seePlans')}
        <ChevronRight className="h-2.5 w-2.5" />
      </Link>
    </div>
  );
};
